import { useCallback, useState } from 'react';

import { Box, Button, getCSSVariable, Text } from '@opendoor/bricks-next';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { IconShieldLock, IconTruck } from '@tabler/icons-react';

import { PropertyDetailsFragment } from '__generated__/athena';

import { useTrackProperty } from 'components/marketplace/helpers';
import { determinePropertyActionability } from 'components/marketplace/helpers/propertyActionability';

import { useObservability } from '../../../helpers/observability';
import { CONSUMER_URL } from '../../globals';
import { TourWithAgentModal } from './PropertySection/TourWithAgentModal';

export const BuyDirectCTA = ({ property }: { property: PropertyDetailsFragment }) => {
  const addressToken = property.addressToken;
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const trackOnBuyDirectClick = useCallback(() => {
    trackProperty('property-buy-direct', 'marketplace-pdp');
    trackJourneyEvent('marketplaceProperty', 'offerStart', {
      addressUuid: addressToken,
      source: 'above-the-fold',
    });
  }, []);

  const trackOnSelfTourClick = useCallback(() => {
    trackProperty('property-self-tour', 'marketplace-pdp');
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: addressToken,
      placement: 'above-the-fold',
    });
  }, []);

  const { tourKind, showBuyDirect, disableBuyDirect } = determinePropertyActionability(property);

  const handleShareHomeClick = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  // Tour with agent case
  if (tourKind === 'tourWithAgent') {
    return (
      <>
        <Box
          backgroundColor="$backgroundPrimary"
          borderWidth={1}
          borderColor="$borderPrimary"
          borderRadius="$8x"
          paddingHorizontal="$10x"
          paddingVertical="$12x"
          flexDirection="column"
          gap={27}
          $platform-web={{
            boxShadow:
              '0px 10px 22px 0px rgba(0, 0, 0, 0.04), 0px 2px 12px 0px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Box flexDirection="column" gap="$4x">
            <Text
              tag="p"
              typography="$subheaderXsmall"
              color="$contentPrimary"
              fontWeight="$medium"
            >
              Visit with your agent
            </Text>
            <Text tag="p" typography="$bodyMedium" color="$contentSecondary">
              Share this home with your agent and have them arrange a time to tour the home.
            </Text>
          </Box>
          <Box flexDirection="column" gap="$6x" alignItems="center">
            <Button
              analyticsName="cosmos-marketplace-tour-with-agent-cta"
              variant="primary"
              size="small"
              onPress={handleShareHomeClick}
              label="Share home"
              width="100%"
            />
          </Box>
        </Box>
        <TourWithAgentModal property={property} isOpen={isDialogOpen} onClose={handleClose} />
      </>
    );
  }

  if (!showBuyDirect) {
    return (
      <Box
        backgroundColor="$backgroundPrimary"
        borderWidth={1}
        borderColor="$borderPrimary"
        borderRadius="$8x"
        paddingHorizontal="$10x"
        paddingVertical="$12x"
        flexDirection="column"
        gap={27}
        $platform-web={{
          boxShadow: '0px 10px 22px 0px rgba(0, 0, 0, 0.04), 0px 2px 12px 0px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Box flexDirection="column" gap="$4x">
          <Text tag="p" typography="$subheaderXsmall" color="$contentPrimary" fontWeight="$medium">
            Take a tour
          </Text>
          <Text tag="p" typography="$bodyMedium" color="$contentSecondary">
            Unlock the door with the Opendoor app yourself, no agent needed.
          </Text>
        </Box>
        <Box flexDirection="column" gap="$6x" alignItems="center">
          <Button
            analyticsName="cosmos-marketplace-self-tour-cta"
            variant="primary"
            size="small"
            onPress={trackOnSelfTourClick}
            target="_blank"
            rel="noopener"
            label="Self-tour"
            width="100%"
            href={`${CONSUMER_URL}/dashboard/buy/tour?id=${addressToken}`}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box flexDirection="column" position="relative">
      {/* Blue Banner at Top */}
      <Box
        backgroundColor="$backgroundAccentBrand"
        borderTopLeftRadius="$8x"
        borderTopRightRadius="$8x"
        paddingTop="$6x"
        paddingBottom="$14x"
        paddingHorizontal="$8x"
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        gap="$4x"
        $platform-web={{
          boxShadow:
            '0px 10px 22px 0px rgba(0, 65, 231, 0.04), 0px 2px 12px 0px rgba(0, 65, 231, 0.04)',
        }}
      >
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M7.73546 3.66437C8.08818 3.74912 8.08818 4.25088 7.73546 4.33563C6.05325 4.73982 4.73983 6.05325 4.33563 7.73546C4.25088 8.08818 3.74912 8.08818 3.66437 7.73546C3.26017 6.05325 1.94675 4.73982 0.26454 4.33563C-0.0881799 4.25088 -0.0881799 3.74912 0.26454 3.66437C1.94675 3.26018 3.26018 1.94675 3.66437 0.26454C3.74912 -0.0881799 4.25088 -0.0881799 4.33563 0.26454C4.73983 1.94675 6.05325 3.26018 7.73546 3.66437Z"
            fill="white"
          />
        </svg>
        <Text tag="p" typography="$bodySmall" color="$contentInversePrimary" fontWeight="$regular">
          Available to buy online today!
        </Text>
      </Box>

      {/* White Card */}
      <Box
        backgroundColor="$backgroundPrimary"
        borderWidth={1}
        borderColor="$borderPrimary"
        borderRadius="$8x"
        paddingHorizontal="$12x"
        paddingBottom="$12x"
        paddingTop="$12x"
        flexDirection="column"
        gap="$12x"
        marginTop={-16}
        zIndex={1}
        $platform-web={{
          boxShadow: '0px 10px 22px 0px rgba(0, 0, 0, 0.04), 0px 2px 12px 0px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header with Price */}
        <Box flexDirection="column" gap="$6x">
          <Text tag="p" typography="$labelLarge" color="$contentPrimary" fontWeight="$medium">
            Opendoor Price
          </Text>
          <Box flexDirection="row" alignItems="center" gap="$8x">
            <Text
              tag="h3"
              typography="$headerSmall"
              color="$contentStateErrorDefault"
              fontWeight="$medium"
            >
              {formatCurrency(property.buyDirectPrice!)}
            </Text>
            {property.listingMetadata?.buyDirectDiscountPercent && (
              <Box
                backgroundColor="$backgroundStateErrorSecondary"
                borderRadius="$6x"
                paddingVertical="$3x"
                paddingHorizontal="$6x"
                $smallerThanLG={{ display: 'none' }}
              >
                <Text
                  tag="span"
                  typography="$bodyMedium"
                  color="$contentStateErrorDefault"
                  fontWeight="$medium"
                  $smallerThanLG={{ typography: '$labelSmall' }}
                >
                  Buy Now Discount
                </Text>
              </Box>
            )}
          </Box>
          {property.estimatedBuyDirectMonthlyPayment ? (
            <Box flexDirection="row" alignItems="center" gap="$2x">
              <Text tag="p" typography="$bodySmall" color="$contentSecondary">
                As low as {formatCurrency(property.estimatedBuyDirectMonthlyPayment)}/month est.
              </Text>
            </Box>
          ) : null}
        </Box>

        {/* Features */}
        <Box
          flexDirection="row"
          justifyContent="space-between"
          gap="$8x"
          $smallerThanLG={{ display: 'none' }}
        >
          <Box flexDirection="row" alignItems="center" gap="$4x">
            <IconTruck size={20} color={getCSSVariable('$contentPrimary', 'color')} />
            <Text tag="p" typography="$bodySmall" color="$contentPrimary">
              Early Move-In
            </Text>
          </Box>
          <Box flexDirection="row" alignItems="center" gap="$4x">
            <IconShieldLock size={20} color={getCSSVariable('$contentPrimary', 'color')} />
            <Text tag="p" typography="$bodySmall" color="$contentPrimary">
              100-Day Warranty
            </Text>
          </Box>
          <Box flexDirection="row" alignItems="center" gap="$4x">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.1499 2.95386C15.3007 2.95389 16.2337 3.88712 16.2339 5.03784V9.84644C16.2482 9.89887 16.2583 9.95354 16.2583 10.0105C16.2583 10.0672 16.2481 10.1214 16.2339 10.1736V14.99C16.2338 16.1409 15.3008 17.0739 14.1499 17.074H5.85791C4.70702 17.074 3.77397 16.1409 3.77393 14.99V5.03784C3.77413 3.88709 4.70712 2.95386 5.85791 2.95386H14.1499ZM5.85791 4.20386C5.39767 4.20386 5.02413 4.57765 5.02393 5.03784V14.99L5.02783 15.075C5.06757 15.4673 5.37973 15.7791 5.77197 15.8191L5.85791 15.823H14.1499C14.5815 15.823 14.9364 15.4953 14.979 15.075L14.9839 14.99V10.6355H9.89307L12.8052 13.5466C13.0493 13.7908 13.0493 14.1873 12.8052 14.4314C12.561 14.6754 12.1645 14.6754 11.9204 14.4314L7.94189 10.4529C7.69779 10.2088 7.69787 9.81227 7.94189 9.56812L11.9204 5.5896C12.1646 5.34545 12.561 5.34545 12.8052 5.5896C13.0493 5.83375 13.0493 6.23024 12.8052 6.47437L9.89307 9.3855H14.9839V5.03784C14.9837 4.57767 14.6101 4.20389 14.1499 4.20386H5.85791Z"
                fill={getCSSVariable('$contentPrimary', 'color')}
              />
            </svg>
            <Text tag="p" typography="$bodySmall" color="$contentPrimary">
              Free Cancellation
            </Text>
          </Box>
        </Box>

        {/* Buttons */}
        <Box flexDirection="column" gap="$6x" alignItems="center">
          <Box flexDirection="column" gap="$8x" width="100%">
            <Button
              analyticsName="cosmos-marketplace-self-tour-cta"
              variant="secondary"
              size="small"
              onPress={trackOnSelfTourClick}
              target="_blank"
              rel="noopener"
              label="Self-tour"
              href={`${CONSUMER_URL}/dashboard/buy/tour?id=${addressToken}`}
            />
            <Button
              analyticsName="cosmos-marketplace-buy-direct-cta"
              variant="primary"
              size="small"
              onPress={trackOnBuyDirectClick}
              rel="noopener"
              label={disableBuyDirect ? 'Offer Pending' : 'Buy this home'}
              href={`${CONSUMER_URL}/dashboard/buy/checkout?id=${addressToken}`}
              disabled={disableBuyDirect}
            />
          </Box>

          {/* Disclaimer */}
          <Box flexDirection="row" justifyContent="center">
            <Text tag="p" typography="$bodySmall" color="$contentSecondary" textAlign="center">
              You will not be charged yet.
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
